/**
 * What "Delete the drops outside the window" will do, in plain lines: which
 * drops go (by row number) against the window, Danish first; which stay and
 * why — outside only until a clock change, sent anyway, a route test's first
 * drop, or a later drop that would move; and the drops before the window
 * opens, which the start fixes, not a delete — as it may when the delete
 * would leave no drop on.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { type ClockCtx, clockText, windowText } from '../../../../../utils/clock/dk-label';
import { useCountryClock } from '../../../../shared/time/country-clock-context';
import type { OutsideRemovalModel } from '../../hooks/use-outside-removal';

interface Props {
  model: OutsideRemovalModel;
  /** "#27–33" — the drops to delete, by their row number. */
  positions: string;
}

const one = (n: number) => n === 1;

export const OutsideRemovalBody: FC<Props> = ({ model, positions }) => {
  const clock = useCountryClock();
  const n = model.remove.length;
  return (
    <div className="flex flex-col gap-2 text-xs leading-snug" data-testid="outside-removal-body">
      <GoLines model={model} positions={positions} clock={clock} />
      <KeptLines model={model} />
      {model.leading > 0 ? <LeadingLine model={model} clock={clock} /> : null}
      {n > 0 || model.startFix ? (
        <p className="text-[11px]">Nothing is {n > 0 ? 'deleted' : 'changed'} until you Save — Cancel undoes it.</p>
      ) : null}
    </div>
  );
};

/** The drops that go, against the window — and when that leaves none on. */
const GoLines: FC<Props & { clock: ClockCtx }> = ({ model, positions, clock }) => {
  const n = model.remove.length;
  const window = <b className="font-medium text-foreground">{windowText(model.window.startMin, model.window.endMin, clock)}</b>;
  if (n === 0) return <p>The window: {window}.</p>;
  return (
    <>
      <p>
        <b className="font-medium text-foreground">Drop{one(n) ? '' : 's'} {positions}</b> {one(n) ? 'is' : 'are'} outside the window {window}
        {model.pastEnd > 0 ? ' or past the end time' : ''} on every day of the next year, clock changes included
        {model.pastEnd > 0 ? ` — ${model.pastEnd} past the end today` : ''}.
      </p>
      {model.leavesNone ? (
        <p>That leaves no drop on — the container sends nothing.{model.startFix ? ` Or instead: ${startAtOpen(model, clock)}` : ''}</p>
      ) : null}
    </>
  );
};

/** The drops outside today that stay, and why. */
const KeptLines: FC<{ model: OutsideRemovalModel }> = ({ model }) => {
  const { seasonal, sentAnyway, held } = model;
  return (
    <>
      {seasonal ? (
        <p>
          {seasonal.count} {one(seasonal.count) ? 'is' : 'are'} outside only until {seasonal.insideFrom} — kept.
        </p>
      ) : null}
      {model.routeTest ? <p>The route test’s drops go before the window on purpose — kept.</p> : null}
      {sentAnyway > 0 ? (
        <p>
          {sentAnyway} {one(sentAnyway) ? 'goes' : 'go'} anyway (🌙 Send it anyway) — kept.
        </p>
      ) : null}
      {held > 0 ? (
        <p>
          {held} {one(held) ? 'stays' : 'stay'}: deleting {one(held) ? 'it' : 'them'} would move a later drop that goes.
        </p>
      ) : null}
    </>
  );
};

/** Drops before the window opens: deleting one pulls the next into its tick — the start is the fix. */
const LeadingLine: FC<{ model: OutsideRemovalModel; clock: ClockCtx }> = ({ model, clock }) => {
  const n = model.leading;
  return (
    <p className="rounded-md border border-warning/40 bg-warning/5 px-2 py-1.5">
      {n} drop{one(n) ? ' sits' : 's sit'} before the window opens — deleting {one(n) ? 'it' : 'them'} would pull the next drops into{' '}
      {one(n) ? 'its' : 'their'} place.{' '}
      {model.startFix ? startAtOpen(model, clock) : 'Move the start in Schedule — a route test’s first drop must stay before the window.'}
    </p>
  );
};

/** "Start at window open puts the first drop at …" — and what it would still leave outside at the end. */
function startAtOpen(model: OutsideRemovalModel, clock: ClockCtx): string {
  const fix = model.startFix;
  if (!fix) return '';
  const after = fix.outsideAfter > 0 ? `, then ${fix.outsideAfter} at the end fall${one(fix.outsideAfter) ? 's' : ''} outside the window` : '';
  return `Start at window open puts the first drop at ${clockText(model.window.startMin, clock)}${after}.`;
}
